/**
 * React-pdf document template for a veterinary prescription label.
 * Targets a 60mm × 86mm label (presized sticky labels for thermal printers).
 *
 * This file is purely rendering — data assembly lives in use-print-labels.ts.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

const LABEL_WIDTH_MM = 60
const LABEL_HEIGHT_MM = 86

/** Convert mm to PDF points (1mm ≈ 2.835pt). */
const mm = (value: number) => value * 2.835

const styles = StyleSheet.create({
  page: {
    width: mm(LABEL_WIDTH_MM),
    height: mm(LABEL_HEIGHT_MM),
    padding: mm(3),
    fontFamily: 'Helvetica',
    fontSize: 6,
    lineHeight: 1.3,
  },
  header: {
    borderBottomWidth: 0.5,
    borderBottomColor: '#000',
    paddingBottom: mm(1.5),
    marginBottom: mm(1.5),
    alignItems: 'center',
  },
  clinicName: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    marginBottom: mm(0.5),
  },
  clinicDetail: {
    fontSize: 6,
    color: '#333',
  },
  section: {
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
    paddingBottom: mm(1.5),
    marginBottom: mm(1.5),
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bold: {
    fontFamily: 'Helvetica-Bold',
  },
  medication: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
  },
  directions: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
  },
  batchLine: {
    fontSize: 5,
  },
  footer: {
    marginTop: 'auto',
    alignItems: 'center',
  },
  warning: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
  },
})

/**
 * Pre-formatted data for rendering a prescription label.
 *
 * Field values are display-ready strings — the caller is responsible for
 * assembling them from domain entities (Patient, Treatment, Product, etc.).
 */
export interface PrescriptionLabelData {
  clinic: {
    name: string
    address: string
  }
  patient: {
    name: string
    species: string
    breed: string | null
    ownerName: string
    /** Most recent weight as a formatted string, e.g. "4.2 kg". Sourced from Vitals. */
    weight: string | null
  }
  prescription: {
    date: string
    /** Product name — the medication being prescribed. */
    medication: string
    /** Client-facing dosage/administration instructions (from Treatment.instructions). */
    directions: string | null
    quantity: string
    repeatsAllowed: number | null
    /** Stock batch allocations — one entry per batch used for this dispensing. */
    batches: { batchNumber: string; expiryDate: string | null }[]
  }
  prescriber: {
    name: string
    qualification: string | null
    /** Display-ready registration line, e.g. "RCVS number 1234567". Null when unavailable. */
    licenseNumber: string | null
  }
}

/**
 * Renders a single prescription label as a one-page PDF document.
 */
export function PrescriptionLabel({ data }: { data: PrescriptionLabelData }) {
  return (
    <Document>
      <PrescriptionLabelPage data={data} />
    </Document>
  )
}

/**
 * Renders multiple prescription labels as pages within a single PDF document.
 * Use this when printing a batch of labels in one go.
 */
export function MultiPrescriptionLabel({ labels }: { labels: PrescriptionLabelData[] }) {
  return (
    <Document>
      {labels.map((data, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static list built at render time
        <PrescriptionLabelPage key={i} data={data} />
      ))}
    </Document>
  )
}

/** Single label page — shared between PrescriptionLabel and MultiPrescriptionLabel. */
function PrescriptionLabelPage({ data }: { data: PrescriptionLabelData }) {
  return (
    <Page size={[mm(LABEL_WIDTH_MM), mm(LABEL_HEIGHT_MM)]} style={styles.page}>
      {/* Clinic header */}
      <View style={styles.header}>
        <Text style={styles.clinicName}>Snoots {data.clinic.name}</Text>
        <Text style={styles.clinicDetail}>{data.clinic.address}</Text>
      </View>

      {/* Patient & owner info */}
      <View style={styles.section}>
        <Text>
          <Text style={styles.bold}>Owner: </Text>
          {data.patient.ownerName}
        </Text>
        <Text>
          <Text style={styles.bold}>Patient: </Text>
          {data.patient.name}
          {data.patient.breed ? ` - ${data.patient.breed}` : ''} ({data.patient.species})
        </Text>
        {data.patient.weight && (
          <Text>
            <Text style={styles.bold}>Weight: </Text>
            {data.patient.weight}
          </Text>
        )}
      </View>

      {/* Medication name */}
      <View style={styles.section}>
        <Text style={styles.medication}>{data.prescription.medication}</Text>
      </View>

      {/* Quantity & details */}
      <View style={styles.section}>
        <View style={styles.row}>
          <Text>
            <Text style={styles.bold}>Qty: </Text>
            {data.prescription.quantity}
          </Text>
          {data.prescription.repeatsAllowed != null && (
            <Text>
              <Text style={styles.bold}>Refills: </Text>
              {data.prescription.repeatsAllowed}
            </Text>
          )}
        </View>
        {data.prescription.batches.length > 0 && (
          <View style={{ marginTop: mm(1) }}>
            {data.prescription.batches.map((b) => (
              <Text key={b.batchNumber} style={styles.batchLine}>
                <Text style={styles.bold}>Batch: </Text>
                {b.batchNumber}
                {b.expiryDate && ` · Exp ${b.expiryDate}`}
              </Text>
            ))}
          </View>
        )}
      </View>

      {/* Directions */}
      {data.prescription.directions && (
        <View style={styles.section}>
          <Text style={styles.directions}>{data.prescription.directions}</Text>
        </View>
      )}

      {/* Prescriber */}
      <View style={styles.section}>
        <View style={styles.row}>
          <Text>
            {data.prescriber.name}
            {data.prescriber.qualification ? `, ${data.prescriber.qualification}` : ''}
          </Text>
          <Text>{data.prescription.date}</Text>
        </View>
        {data.prescriber.licenseNumber && <Text>{data.prescriber.licenseNumber}</Text>}
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.warning}>For animal treatment only</Text>
        <Text style={styles.warning}>Keep out of reach of children</Text>
      </View>
    </Page>
  )
}
